import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Typography, 
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import { CreateProductModal } from './CreateProductModal';
import { AppContext } from '../../AppContext';
import AddIcon from '@material-ui/icons/Add';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AusumConfigForm } from './AusumConfigForm';
import * as ausumConfigurationsService from '../../services/ausumConfigurations.service';
import * as clientService from '../../services/client.service';
import * as ordersService from '../../services/orders.service';

const ConfigurationFormWrapper = ({ config, sweepTypes, clients, products, onSave, onDelete }) => {
  const ftpConfig = config.ausumFtpconfig?.[0] || {};
  const productLookups = [...(config.ausumProductLookup || [])];
  
  // Handle default product mapping
  if (productLookups.length === 0) {
    productLookups.push({
      surveyMethod: null,
      productId: null,
      createdBy: config.createdBy,
      modifiedBy: config.modifiedBy
    });
  } else if (!productLookups.some(p => p.surveyMethod === null)) {
    productLookups.unshift({
      surveyMethod: null,
      productId: null,
      createdBy: config.createdBy,
      modifiedBy: config.modifiedBy
    });
  } else {
    const defaultIndex = productLookups.findIndex(p => p.surveyMethod === null);
    if (defaultIndex > 0) {
      const defaultMapping = productLookups.splice(defaultIndex, 1)[0];
      productLookups.unshift(defaultMapping);
    }
  }

  const formMethods = useForm({
    resolver: yupResolver(formValidationSchema),
    reValidateMode: 'onSubmit',
      defaultValues: {
      ausumSweepConfigId: config.ausumSweepConfigId,
      ausumSweepTypeId: config.ausumSweepTypeId,
      name: config.name || '',
      description: config.description || '',
      referenceNo: config.referenceNo || '',
      createdOn: config.createdOn,
      createdBy: config.createdBy,
      modifiedOn: config.modifiedOn,
      modifiedBy: config.modifiedBy,
      active: config.active ?? true,
      pdfReturn: config.pdfreturn ?? false,
      ausumSweepType: config.ausumSweepType,
      ausumClientLookup: [...(config.ausumClientLookup || [])],
      ausumProductLookup: productLookups,
      ausumFtpconfig: [{
        ausumFtpconfigId: ftpConfig.ausumFtpconfigId,
        ausumSweepConfigId: ftpConfig.ausumSweepConfigId,
        host: ftpConfig.host || '',
        port: ftpConfig.port || '',
        protocol: ftpConfig.protocol || '',
        username: ftpConfig.username || '',
        password: ftpConfig.password || '',
        folderLocation: ftpConfig.folderLocation || '',
        returnFolder: ftpConfig.returnFolder || '',
        archiveFolder: ftpConfig.archiveFolder || '',
        createdOn: ftpConfig.createdOn,
        createdBy: ftpConfig.createdBy,
        modifiedOn: ftpConfig.modifiedOn,
        modifiedBy: ftpConfig.modifiedBy
      }]
    }
  });

  return (
    <FormProvider {...formMethods}>
                      <AusumConfigForm
                  config={config}
                  sweepTypes={sweepTypes}
                  clients={clients}
                  products={products}
                  onSave={onSave}
                  onDelete={onDelete}
                />
    </FormProvider>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    minHeight: '100%',
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
  },
}));

const formValidationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  description: Yup.string().required('Required'),
  referenceNo: Yup.string(),
  ausumSweepTypeId: Yup.number().required('Required'),
  active: Yup.boolean().required('Required'),
  pdfReturn: Yup.boolean().nullable(),
  ausumClientLookup: Yup.array().of(
    Yup.object().shape({
      branchNumber: Yup.string().required('Required'),
      clientId: Yup.number().required('Required'),
    })
  ),
  ausumFtpconfig: Yup.array().when('ausumSweepTypeId', {
    is: 2,
    then: Yup.array().of(
      Yup.object().shape({
        host: Yup.string().required('Required'),
        port: Yup.number().required('Required').min(1, 'Must be greater than 0').max(65535, 'Must be less than 65536'),
        protocol: Yup.string().required('Required').oneOf(['ftp', 'sftp', 'ftps'], 'Invalid protocol'),
        username: Yup.string().required('Required'),
        password: Yup.string().required('Required'),
        folderLocation: Yup.string().required('Required'),
      })
    ).min(1, 'FTP configuration is required'),
    otherwise: Yup.array(),
  }),
});

export const AusumConfigurations = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [configurations, setConfigurations] = useState([]);
  const [sweepTypes, setSweepTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
  const { dispatch } = useContext(AppContext);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [configsData, sweepTypesData, clientsData, productsData] = await Promise.all([
        ausumConfigurationsService.getConfigurations(),
        ausumConfigurationsService.getSweepTypes(),
        clientService.getClientList(),
        ordersService.getAllReportCodes()
      ]);
      setConfigurations(configsData);
      setSweepTypes(sweepTypesData);
      setClients(clientsData);
      setProducts(productsData);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      dispatch({
        type: "SET_SNACKBAR",
        snackbar: { isOpen: true, message: 'Failed to load data', variant: 'error' }
      });
    } finally {
      setLoading(false);
    }
  };

    return (
    <div className={classes.root}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateProductModalOpen(true)}
            >
              Create New Product
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                const newConfig = {
                  ausumSweepConfigId: 0,
                  ausumSweepTypeId: null,
                  name: '',
                  description: '',
                  referenceNo: '',
                  active: true,
                  pdfReturn: false,
                  createdBy: 'system',
                  modifiedBy: 'system',
                  ausumSweepType: null,
                  ausumClientLookup: [],
                  ausumFtpconfig: [],
                  ausumProductLookup: []
                };
                setConfigurations([...configurations, newConfig]);
              }}
            >
              Add Configuration
            </Button>
          </div>
          <Grid container spacing={2}>
            {/* Active Configurations */}
            {configurations
              .filter(config => config.active)
              .map((config) => (
            <Grid item xs={12} key={config.ausumSweepConfigId}>
              <ConfigurationFormWrapper
                config={config}
                sweepTypes={sweepTypes}
                clients={clients}
                products={products}
                onSave={async (updatedConfig) => {
                  try {
                    if (updatedConfig.ausumSweepConfigId === 0) {
                      // New configuration - use POST
                      await ausumConfigurationsService.createConfiguration(updatedConfig);
                      dispatch({
                        type: "SET_SNACKBAR",
                        snackbar: { isOpen: true, message: 'Configuration created successfully', variant: 'success' }
                      });
                    } else {
                      // Existing configuration - use PUT
                      await ausumConfigurationsService.updateConfiguration(updatedConfig.ausumSweepConfigId, updatedConfig);
                      dispatch({
                        type: "SET_SNACKBAR",
                        snackbar: { isOpen: true, message: 'Configuration updated successfully', variant: 'success' }
                      });
                    }
                    // Refresh the data
                    await loadInitialData();
                  } catch (err) {
                    console.error('Failed to save configuration:', err);
                    // Error response will already be parsed by the service
                    const errorMessage = err.message || 'Failed to save configuration';
                    dispatch({
                      type: "SET_SNACKBAR",
                      snackbar: { isOpen: true, message: errorMessage, variant: 'error' }
                    });
                  }
                }}
                onDelete={(id) => console.log('Delete:', id)}
              />
            </Grid>
          ))}

            {/* Inactive Configurations */}
            {configurations.some(config => !config.active) && (
              <Grid item xs={12}>
                <div style={{ margin: '24px 0' }}>
                  <Divider />
                  <Typography variant="h6" style={{ margin: '16px 0', color: '#666' }}>
                    Inactive Configurations
                  </Typography>
                </div>
              </Grid>
            )}

            {configurations
              .filter(config => !config.active)
              .map((config) => (
                <Grid item xs={12} key={config.ausumSweepConfigId}>
                  <ConfigurationFormWrapper
                    config={config}
                    sweepTypes={sweepTypes}
                    clients={clients}
                    products={products}
                    onSave={async (updatedConfig) => {
                      try {
                        if (updatedConfig.ausumSweepConfigId === 0) {
                          await ausumConfigurationsService.createConfiguration(updatedConfig);
                          dispatch({
                            type: "SET_SNACKBAR",
                            snackbar: { isOpen: true, message: 'Configuration created successfully', variant: 'success' }
                          });
                        } else {
                          await ausumConfigurationsService.updateConfiguration(updatedConfig.ausumSweepConfigId, updatedConfig);
                          dispatch({
                            type: "SET_SNACKBAR",
                            snackbar: { isOpen: true, message: 'Configuration updated successfully', variant: 'success' }
                          });
                        }
                        await loadInitialData();
                      } catch (err) {
                        console.error('Failed to save configuration:', err);
                        const errorMessage = err.message || 'Failed to save configuration';
                        dispatch({
                          type: "SET_SNACKBAR",
                          snackbar: { isOpen: true, message: errorMessage, variant: 'error' }
                        });
                      }
                    }}
                    onDelete={(id) => console.log('Delete:', id)}
                  />
                </Grid>
              ))}
          </Grid>
        </>
      )}

      <CreateProductModal
        open={isCreateProductModalOpen}
        onClose={() => setIsCreateProductModalOpen(false)}
        onSave={async (productData) => {
          try {
            const newProduct = await ausumConfigurationsService.createProduct(productData);
            
            // Update the products list with the newly created product
            setProducts([...products, newProduct]);
            
            // Close the modal
            setIsCreateProductModalOpen(false);
            
            // Show success message
            dispatch({
              type: "SET_SNACKBAR",
              snackbar: { isOpen: true, message: 'Product created successfully', variant: 'success' }
            });
          } catch (error) {
            console.error('Error creating product:', error);
            dispatch({
              type: "SET_SNACKBAR",
              snackbar: { isOpen: true, message: 'Failed to create product', variant: 'error' }
            });
          }
        }}
      />
    </div>
  );
};